/**
 * Runs a scenario without leaving back testing: the simulator page's SimulatorWorkbench in a
 * right-hand sheet. A sheet covers the list and holds one run at a time, so it offers the
 * way out to a tab of its own.
 */

import { useAtomValue } from 'jotai';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { simulatorSessionAtom } from '@/modules/simulator/session/simulator-session.store';
import { SimulatorWorkbench } from '@/modules/simulator/simulator-workbench';

interface ScenarioRunnerSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Where the scenario on screen lives as a page of its own. */
	runUrl?: (scenarioId: string) => string;
}

export const ScenarioRunnerSheet = ({ open, onOpenChange, runUrl }: ScenarioRunnerSheetProps) => {
	// The loaded case, not the row that opened the sheet: the case loader may have
	// moved on since, and the way out has to lead to what is actually on screen.
	const session = useAtomValue(simulatorSessionAtom);
	const loadedId = session && session.activeIndex !== null ? session.cases[session.activeIndex]?.id : undefined;

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side='right'
				className='flex w-[90vw] flex-col gap-0 bg-background p-0 backdrop-blur-none sm:max-w-[90vw]'
			>
				<SheetHeader className='border-b px-4 py-3'>
					<div className='flex items-center justify-between gap-3'>
						<SheetTitle className='text-base'>Run scenario</SheetTitle>
						{runUrl && loadedId && (
							// An anchor, so a middle click and a cmd-click do what they always do.
							<Button variant='outline' size='sm' className='mr-8 shrink-0 gap-1.5 text-xs' asChild>
								<a href={runUrl(loadedId)} target='_blank' rel='noreferrer'>
									<ExternalLink className='size-3.5' />
									Open in a new tab
								</a>
							</Button>
						)}
					</div>
					{/* For the screen reader, which reaches the dialog before the thread and sidebar that show this. */}
					<SheetDescription className='sr-only'>
						The loaded scenario&apos;s thread and tool world: tweak them and hit Ask AI to run it. Edits here are not saved.
					</SheetDescription>
				</SheetHeader>
				<div className='min-h-0 flex-1'>
					<SimulatorWorkbench variant='runner' />
				</div>
			</SheetContent>
		</Sheet>
	);
};
